import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useErrorNotification } from '@providers/ErrorsNotificationProvider';
import { useQueries } from '@tanstack/react-query';
import { proxy, ref, useSnapshot } from 'valtio';

import { FUNC_NOOP } from '@libs/constants';
import { formatName } from '@libs/formatNames';

import { parseMemberAux } from '@features/club/typings';
import { components } from '@features/shared/api/club/clients';
import { useManagerSocket } from '@features/shared/api/club/hooks';
import {
  roundShortGamesQueryOptions,
  useRoundShortGamesSockets,
} from '@features/shared/api/streams';
import {
  activeTournamentParticipantsQueryOptions,
  tournamentCardQueryOptions,
  useUpdateParticipantAuxMutation,
  useVideoPrivateTicketMutation,
  useVideoRoomTicketMutation,
} from '@features/tournament/api/hooks';
import { parseParticipantAux } from '@features/tournament/types';
import { getActiveRound } from '@features/tournament/utils/tournamentHelpers';
import { MediaDevices, useMediaDevices } from '@features/video/hooks/useMediaDevices';
import { VideoLog } from '@features/video/lib/log';
import { MicSource } from '@features/video/lib/MicSource';
import {
  RefereeRoomClient,
  RefereeRoomEvent,
  RoomDevice,
  RoomSnapshot,
} from '@features/video/lib/RefereeRoomClient';
import { roomKey } from '@features/video/lib/ticket';
import {
  MicIssue,
  RefereeConnection,
  RefereeInfo,
  RefereePlayer,
  RefereeRoomInfo,
  RoomRef,
  Speaking,
} from '@features/video/types';

// Черный список без событий: опрашиваем редко, плюс проверка, когда у игрока
// отключились все устройства
const BAN_LIST_POLL_MS = 30000;

const EMPTY_SNAPSHOT: RoomSnapshot = { devices: [], hosts: [] };

// Сколько камер ждать, если в aux участника клуба не сказано
const DEFAULT_CAMERAS = 1;

// Роли устройств по порядку номеров камер; без роли — последними
const SLOT_ORDER: Record<string, number> = { main: 0, extra: 1 };

// Участник турнира в памяти судьи: то, что нужно списку комнаты
type Member = {
  userId: string;
  name: string;
  status: components['schemas']['ParticipantStatus'];
  videoRoomNumber: number | null;
  inPrivateRoom: boolean;
  // сколько камер игрок должен включать: из aux участника клуба
  camerasRequired: number;
};

// Состояние одной комнаты для страницы: в объекте valtio по ключу комнаты, число комнат
// переменное. Снимок LiveKit внутри как есть (`ref`): в нем потоки и функции
type RoomState = {
  connection: RefereeRoomInfo['connection'];
  error: string | null;
  snapshot: RoomSnapshot;
  audioBlocked: boolean;
};

export type PrivateRoom = {
  player: RefereePlayer;
  connection: Exclude<RefereeConnection, 'setup'>;
  error: string | null;
  referees: RefereeInfo[];
  // трек для индикатора уровня: отвод общего шумодава; null — индикатор берет захват
  meterTrack: MediaStreamTrack | null;
  // горячее: identity говорящих в приватной комнате
  speakingProxy: Speaking;
};

export type RefereeRooms = {
  // сводное: setup — не входили; connecting — ни одна комната не подключилась; live — хоть
  // одна в эфире; reconnecting — хоть одна восстанавливается; error — все отвалились
  connection: RefereeConnection;
  // ошибка, когда отвалились все: первая из комнат
  error?: string | null;
  // комнаты мультикомнаты с их состоянием: плитка в конце сетки
  rooms: RefereeRoomInfo[];
  // микрофон судьи: настройка до входа и в меню шестеренки
  devices: MediaDevices;
  micOn: boolean;
  players: RefereePlayer[];
  referees: RefereeInfo[];
  // горячее: identity говорящих устройств и судей всех комнат; плитки подписываются сами
  speakingProxy: Speaking;
  join: () => void;
  leave: () => void;
  // подключить заново одну отвалившуюся комнату
  retryRoom: (room: RoomRef) => void;
  toggleMic: () => void;
  // микрофон сломался в эфире (звонок, другое приложение, контекст ждет клика); fixMic — клик по плашке
  micIssue: MicIssue;
  fixMic: () => void;
  // браузер не дал играть звук игроков без клика в любой из комнат; fixMic включает и его
  audioBlocked: boolean;
  // трек для индикатора уровня: отвод общего шумодава, мьют его не касается; null —
  // индикатор берет захват
  meterTrack: MediaStreamTrack | null;
  // закрепленные на странице игроки: им снимаем потолок слоя сетки
  setPinned: (userIds: string[]) => void;
  // игроки с плитками на странице: только им подписка на видео
  setShown: (userIds: string[]) => void;
  // действия над игроком: комната его записи, без нее — первая, где он показан
  mute: (userId: string, deviceId: string) => void;
  kick: (userId: string, room?: RoomRef | null) => void;
  restore: (userId: string, room?: RoomRef | null) => void;
  invitePrivate: (userId: string, room?: RoomRef | null) => void;
  returnToRoom: (userId: string, room?: RoomRef | null) => void;
  // открытая модалка приватной комнаты
  privateRoom: PrivateRoom | null;
  openPrivate: (userId: string, room?: RoomRef | null) => void;
  closePrivate: () => void;
};

type Options = {
  clubId: string;
  // комнаты мультикомнаты: одного клуба, первая — из пути страницы
  rooms: RoomRef[];
};

// Судья в одной или нескольких видеокомнатах турниров клуба: по клиенту LiveKit на комнату,
// список игроков каждой комнаты из участников ее турнира, их партии из стрима тура, черный
// список с сервера связи, флаг приватной комнаты по событиям менеджерам. Микрофон один
// на все комнаты (общий источник, каждому клиенту клон чистого трека), приват один на
// мультикомнату: пока он открыт, все общие комнаты замьючены и без звука
export function useRefereeRooms({ clubId, rooms }: Options): RefereeRooms {
  const { report } = useErrorNotification();
  const devices = useMediaDevices({ audio: true, video: false });
  const log = useMemo(() => new VideoLog({ role: 'referee', clubId }), [clubId]);
  // Беды с микрофоном судьи: окружение, в журнал событием; неизвестная — ошибкой
  const { micError, error: mediaError } = devices;
  useEffect(() => {
    if (micError) log.event('device_error', { device: 'microphone', kind: micError });
    if (mediaError?.kind === 'unknown') log.error('device_error', new Error(mediaError.message));
  }, [micError, mediaError, log]);

  const roomsKey = rooms.map(roomKey).join(',');
  const refRooms = useRef(rooms);
  refRooms.current = rooms;
  const tournamentIds = useMemo(
    () => [...new Set(rooms.map((r) => r.tournamentId))],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomsKey],
  );

  // ---- Микрофон: один источник на все комнаты

  const [micSource] = useState(() => new MicSource(log));
  const [micOn, setMicOn] = useState(true);
  const refMicOn = useRef(true);
  const [micIssue, setMicIssue] = useState<MicIssue>(null);
  const [meterTrack, setMeterTrack] = useState<MediaStreamTrack | null>(null);
  // поток, чей микрофон сейчас в источнике: смену устройства видно по новому потоку
  const refPublishedStream = useRef<MediaStream | null>(null);
  // цепочка собрана: комнаты подключаются после
  const refSourceReady = useRef<Promise<void> | null>(null);

  // ---- Комнаты: клиенты по ключу, состояние для страницы

  const [joined, setJoined] = useState(false);
  const [roomsProxy] = useState(() => proxy<Record<string, RoomState>>({}));
  const roomsSnap = useSnapshot(roomsProxy);
  const refClients = useRef(new Map<string, RefereeRoomClient>());
  const [speakingProxy] = useState(() => proxy<Speaking>({}));
  // черные списки по ключу комнаты
  const [banned, setBanned] = useState<Map<string, Set<string>>>(() => new Map());
  // закрепленные и показанные игроки: клиенты создаются и пересоздаются, настройки держим тут
  const refPinned = useRef<string[]>([]);
  const refShown = useRef<string[] | null>(null);
  const { mutateAsync: requestTicket } = useVideoRoomTicketMutation(clubId);

  // ---- Приватная комната

  const [privateUserId, setPrivateUserId] = useState<string | null>(null);
  // комната, из которой открыт приват: запись игрока ищем по ней
  const [privateAt, setPrivateAt] = useState<RoomRef | null>(null);
  const [privateConnection, setPrivateConnection] =
    useState<PrivateRoom['connection']>('connecting');
  const [privateError, setPrivateError] = useState<string | null>(null);
  const [privateAudioBlocked, setPrivateAudioBlocked] = useState(false);
  const [privateSnapshot, setPrivateSnapshot] = useState<RoomSnapshot>(EMPTY_SNAPSHOT);
  const [privateSpeakingProxy] = useState(() => proxy<Speaking>({}));
  const refPrivateClient = useRef<RefereeRoomClient | null>(null);
  // фокус переключен в приват: общие комнаты замьючены и без звука
  const refFocusPrivate = useRef(false);
  // ключ комнаты, где стоит метка «судья в привате с игроком»
  const refPrivateWith = useRef<string | null>(null);
  const { mutateAsync: requestPrivateTicket } = useVideoPrivateTicketMutation(clubId);

  // ---- Участники турниров

  // В памяти по турнирам: список с сервера — затравка, дальше только события менеджерского
  // сокета. Рвался сокет — события могли пропасть, список заново
  const [members, setMembers] = useState<Map<string, Map<string, Member>>>(() => new Map());
  // Назначенные комнаты за время жизни страницы, по турнирам. Событие идемпотентно: копим
  // все и накладываем поверх любой затравки, так назначение не теряется, пока список
  // запрашивается. Подписка объявлена раньше запроса: между снэпшотом и подпиской окна нет
  const refAssigned = useRef(new Map<string, Map<string, number>>());
  const assignedOf = (tournamentId: string) => {
    let map = refAssigned.current.get(tournamentId);
    if (!map) {
      map = new Map();
      refAssigned.current.set(tournamentId, map);
    }
    return map;
  };
  // патч участника: в одном турнире или, без турнира, во всех (настройка на уровне клуба)
  const patchMember = useCallback(
    (tournamentId: string | null, userId: string, patch: Partial<Member>) => {
      setMembers((prev) => {
        let next = prev;
        prev.forEach((map, t) => {
          if (tournamentId !== null && t !== tournamentId) return;
          const member = map.get(userId);
          if (!member) return;
          if (next === prev) next = new Map(prev);
          next.set(t, new Map(map).set(userId, { ...member, ...patch }));
        });
        return next;
      });
    },
    [],
  );

  const participantQueries = useQueries({
    queries: tournamentIds.map((t) => ({
      ...activeTournamentParticipantsQueryOptions(clubId, t, 'AlphabetAsc', true),
      refetchOnWindowFocus: false,
    })),
  });
  const refParticipantQueries = useRef(participantQueries);
  refParticipantQueries.current = participantQueries;
  const reseed = useCallback(
    (tournamentId: string) => {
      const i = tournamentIds.indexOf(tournamentId);
      refParticipantQueries.current[i]?.refetch();
    },
    [tournamentIds],
  );
  // Затравка по новым данным турнира; без зависимостей: работа только когда данные сменились
  const refSeeded = useRef(new Map<string, unknown>());
  useEffect(() => {
    participantQueries.forEach((q, i) => {
      const t = tournamentIds[i];
      if (!t || !q.data || refSeeded.current.get(t) === q.data) return;
      refSeeded.current.set(t, q.data);
      const seeded = seedMembers(q.data, assignedOf(t));
      setMembers((prev) => new Map(prev).set(t, seeded));
    });
  });
  const { mutateAsync: updateAux } = useUpdateParticipantAuxMutation(clubId);

  useManagerSocket(clubId, (e) => {
    if (e.eventType === 'SocketReconnected') {
      log.event('socket_reconnected');
      tournamentIds.forEach(reseed);
      return;
    }
    // настройка игрока на уровне клуба: требование к числу камер
    if (e.eventType === 'ClubMemberAuxUpdated') {
      patchMember(null, e.userId, {
        camerasRequired: parseMemberAux(e.aux).camerasRequired ?? DEFAULT_CAMERAS,
      });
      return;
    }
    if (!('tournamentId' in e) || !tournamentIds.includes(e.tournamentId)) return;
    const t = e.tournamentId;
    switch (e.eventType) {
      case 'ClubParticipantAuxUpdated':
        patchMember(t, e.userId, {
          inPrivateRoom: parseParticipantAux(e.aux).privateVideoRoom === true,
        });
        break;
      // номер комнаты появляется, когда участнику выдан билет: пришедший без камеры виден
      // как «Не подключен». Участника нет в затравке (записался позже) — имя есть только в списке
      case 'ClubParticipantVideoRoomSet':
        if (e.roomNumber === undefined) break;
        assignedOf(t).set(e.userId, e.roomNumber);
        if (members.get(t)?.has(e.userId)) {
          patchMember(t, e.userId, { videoRoomNumber: e.roomNumber });
        } else {
          reseed(t);
        }
        break;
      case 'ParticipantWasBanned':
        patchMember(t, e.userId, { status: 'Banned' });
        break;
      case 'ParticipantWasRemoved':
        patchMember(t, e.userId, { status: 'Removed' });
        break;
    }
  });

  // ---- Карточки турниров и партии текущих туров

  const cardQueries = useQueries({
    queries: tournamentIds.map((t) => tournamentCardQueryOptions(clubId, t)),
  });
  const titles = new Map(tournamentIds.map((t, i) => [t, cardQueries[i]?.data?.title ?? '']));
  const roundsSig = tournamentIds
    .map((t, i) => {
      const rounds = cardQueries[i]?.data?.rounds ?? [];
      return `${t}/${rounds.length > 0 ? getActiveRound(rounds) : 0}`;
    })
    .join(',');
  const rounds = useMemo(
    () =>
      roundsSig
        .split(',')
        .filter(Boolean)
        .map((pair) => {
          const at = pair.lastIndexOf('/');
          return { tournamentId: pair.slice(0, at), roundNumber: Number(pair.slice(at + 1)) };
        }),
    [roundsSig],
  );
  const gameQueries = useQueries({
    queries: rounds.map((r) => ({
      ...roundShortGamesQueryOptions(r.tournamentId, r.roundNumber),
      enabled: r.roundNumber > 0,
      refetchOnWindowFocus: false,
    })),
  });
  useRoundShortGamesSockets(rounds);
  const gamesSig = gameQueries.map((q) => q.dataUpdatedAt).join(',');
  const gamesByTournament = useMemo(
    () => new Map(rounds.map((r, i) => [r.tournamentId, indexGames(gameQueries[i]?.data)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rounds, gamesSig],
  );

  // ---- Источник микрофона: события

  const allClients = useCallback(() => {
    const clients = [...refClients.current.values()];
    if (refPrivateClient.current) clients.push(refPrivateClient.current);
    return clients;
  }, []);

  useEffect(
    () =>
      micSource.events.subscribe((e) => {
        if (e.type === 'track') {
          // новый чистый трек: каждой комнате свой клон
          allClients().forEach((c) => c.replaceMic(e.track.clone()).catch(FUNC_NOOP));
        } else if (e.type === 'issue') {
          setMicIssue(e.issue);
          allClients().forEach((c) => c.setGestureNeeded(e.issue === 'gesture'));
        } else {
          setMeterTrack(e.track);
        }
      }),
    [micSource, allClients],
  );

  // Судья выбрал другой микрофон: хук устройств дал новый поток, а старый трек остановил —
  // источнику новый сырой трек, клоны разойдутся по комнатам сами
  useEffect(() => {
    const stream = devices.stream;
    if (!joined || !stream || refPublishedStream.current === stream) return;
    refPublishedStream.current = stream;
    refSourceReady.current = micSource.attach(stream.getAudioTracks()[0] ?? null);
  }, [devices.stream, joined, micSource]);

  // ---- Комнаты: подключение и отключение

  const refreshBanList = useCallback((key: string) => {
    refClients.current
      .get(key)
      ?.banList()
      .then((list) => setBanned((prev) => new Map(prev).set(key, new Set(list))))
      .catch(FUNC_NOOP);
  }, []);

  const openRoom = useCallback(
    (room: RoomRef) => {
      const key = roomKey(room);
      if (refClients.current.has(key)) return;
      const client = new RefereeRoomClient(
        log.child({ tournamentId: room.tournamentId, roomNumber: room.roomNumber }),
        true,
        speakingProxy,
      );
      refClients.current.set(key, client);
      client.setPinned(refPinned.current);
      client.setShown(refShown.current);
      client.setGestureNeeded(micSource.gestureNeeded);
      // приват уже открыт: новая комната сразу в фоне
      if (refFocusPrivate.current) {
        client.setAudioOn(false);
        client.setVideoOn(false);
      }
      roomsProxy[key] = {
        connection: 'connecting',
        error: null,
        snapshot: ref(EMPTY_SNAPSHOT),
        audioBlocked: false,
      };
      client.events.subscribe((e: RefereeRoomEvent) => {
        const state = roomsProxy[key];
        if (!state) return;
        if (e.type === 'status') state.connection = e.status;
        else if (e.type === 'snapshot') state.snapshot = ref(e.snapshot);
        else if (e.type === 'audio-blocked') state.audioBlocked = e.blocked;
        else {
          // связь потеряна: клиент отпускаем, «Повторить» создаст новый
          client.disconnect();
          refClients.current.delete(key);
          state.connection = 'error';
          state.error = e.reason === 'ended' ? 'Комната закрыта' : 'Связь с комнатой потеряна';
          state.snapshot = ref(EMPTY_SNAPSHOT);
          state.audioBlocked = false;
        }
      });
      const micOnNow = refMicOn.current && !refFocusPrivate.current;
      Promise.all([requestTicket(room), refSourceReady.current])
        .then(([ticket]) => client.connect(ticket, micSource.clone(), micOnNow))
        .then(() => refreshBanList(key))
        .catch((e: unknown) => {
          client.disconnect();
          refClients.current.delete(key);
          const state = roomsProxy[key];
          if (!state) return;
          state.connection = 'error';
          state.error = e instanceof Error ? e.message : 'Не удалось подключиться';
        });
    },
    [log, speakingProxy, micSource, roomsProxy, requestTicket, refreshBanList],
  );

  const closeRoom = useCallback(
    (key: string) => {
      refClients.current.get(key)?.disconnect();
      refClients.current.delete(key);
      delete roomsProxy[key];
      setBanned((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      if (refPrivateWith.current === key) refPrivateWith.current = null;
    },
    [roomsProxy],
  );

  // Состав комнат: новые подключаем, убранные отключаем; отвалившуюся заново не трогаем,
  // ее поднимает «Повторить»
  useEffect(() => {
    if (!joined) return;
    const wanted = new Map(refRooms.current.map((r) => [roomKey(r), r]));
    Object.keys(roomsProxy).forEach((key) => {
      if (!wanted.has(key)) closeRoom(key);
    });
    wanted.forEach((room, key) => {
      if (!roomsProxy[key]) openRoom(room);
    });
  }, [joined, roomsKey, roomsProxy, openRoom, closeRoom]);

  const join = useCallback(() => {
    if (joined) return;
    log.event('join', { rooms: refRooms.current.length });
    // контекст шумодава рождается в клике — running
    micSource.wake();
    micSource.load();
    const stream = devices.stream;
    refPublishedStream.current = stream;
    refSourceReady.current = micSource.attach(stream?.getAudioTracks()[0] ?? null);
    setJoined(true);
  }, [joined, log, micSource, devices.stream]);

  const retryRoom = useCallback(
    (room: RoomRef) => {
      const key = roomKey(room);
      if (refClients.current.has(key)) return;
      delete roomsProxy[key];
      openRoom(room);
    },
    [roomsProxy, openRoom],
  );

  const resetPrivate = useCallback(() => {
    refPrivateClient.current?.disconnect();
    refPrivateClient.current = null;
    setPrivateUserId(null);
    setPrivateAt(null);
    setPrivateSnapshot(EMPTY_SNAPSHOT);
    setPrivateAudioBlocked(false);
  }, []);

  // Выход: метаданные и фокус комнат не трогаем, участник исчезает вместе с ними
  const leave = useCallback(() => {
    resetPrivate();
    refFocusPrivate.current = false;
    Object.keys(roomsProxy).forEach(closeRoom);
    micSource.attach(null).catch(FUNC_NOOP);
    refSourceReady.current = null;
    refPublishedStream.current = null;
    setJoined(false);
    setMicIssue(null);
    setMeterTrack(null);
  }, [resetPrivate, roomsProxy, closeRoom, micSource]);

  // Уход со страницы: отпускаем все комнаты и микрофон
  useEffect(
    () => () => {
      refPrivateClient.current?.disconnect();
      refClients.current.forEach((c) => c.disconnect());
      micSource.destroy();
    },
    [micSource],
  );

  // Клик по «Включить микрофон»: захват отобрали — перезапрашиваем устройства, новый поток
  // уйдет во все комнаты; контекст шумодава уснул — будим его прямо в жесте
  const fixMic = useCallback(() => {
    if (micIssue === 'lost') devices.retry();
    else micSource.wakeMic();
    allClients().forEach((c) => c.startAudio());
  }, [micIssue, devices, micSource, allClients]);

  const toggleMic = useCallback(() => {
    const next = !micOn;
    setMicOn(next);
    refMicOn.current = next;
    micSource.setMicOn(next);
    // микрофон уходит туда, где фокус: в приват или во все общие комнаты
    const targets = refFocusPrivate.current
      ? [refPrivateClient.current]
      : [...refClients.current.values()];
    targets.forEach((c) => c?.setMicOn(next).catch(FUNC_NOOP));
  }, [micOn, micSource]);

  // Черные списки: редкий опрос
  useEffect(() => {
    if (!joined) return;
    const id = window.setInterval(
      () => refClients.current.forEach((_c, key) => refreshBanList(key)),
      BAN_LIST_POLL_MS,
    );
    return () => window.clearInterval(id);
  }, [joined, refreshBanList]);

  // У игрока отключились все устройства — может, его забанил другой судья
  const refUids = useRef(new Map<string, Set<string>>());
  useEffect(() => {
    Object.entries(roomsSnap).forEach(([key, state]) => {
      const uids = new Set(state.snapshot.devices.map((d) => d.uid));
      const was = refUids.current.get(key);
      refUids.current.set(key, uids);
      if (was && [...was].some((uid) => !uids.has(uid))) refreshBanList(key);
    });
  }, [roomsSnap, refreshBanList]);

  // ---- Приватная комната

  const refPlayers = useRef<RefereePlayer[]>([]);
  // Комната записи игрока: плитка знает свою; ссылка на приват комнаты не несет — берем
  // запись с камерами, без них первую: один человек может быть записан в несколько турниров
  const roomOf = useCallback((userId: string, room?: RoomRef | null): RoomRef | null => {
    if (room) return room;
    const entries = refPlayers.current.filter((x) => x.userId === userId);
    const p = entries.find((x) => x.devices.length > 0) ?? entries[0];
    return p?.tournamentId !== undefined && p.roomNumber !== undefined
      ? { tournamentId: p.tournamentId, roomNumber: p.roomNumber }
      : null;
  }, []);

  const openPrivate = useCallback(
    (userId: string, at?: RoomRef | null) => {
      const room = roomOf(userId, at);
      const client = room && refClients.current.get(roomKey(room));
      if (!room || !client || refPrivateClient.current) return;
      log.event('open_private', { playerId: userId, tournamentId: room.tournamentId });
      const privateClient = new RefereeRoomClient(
        log.child({ private: true, playerId: userId, tournamentId: room.tournamentId }),
        false,
        privateSpeakingProxy,
      );
      refPrivateClient.current = privateClient;
      privateClient.setGestureNeeded(micSource.gestureNeeded);
      setPrivateUserId(userId);
      setPrivateAt(room);
      setPrivateError(null);
      setPrivateConnection('connecting');
      setPrivateSnapshot(EMPTY_SNAPSHOT);
      const dropped = (message: string) => {
        privateClient.disconnect();
        refPrivateClient.current = null;
        setPrivateConnection('error');
        setPrivateAudioBlocked(false);
        setPrivateError(message);
      };
      privateClient.events.subscribe((e: RefereeRoomEvent) => {
        if (e.type === 'status') setPrivateConnection(e.status);
        else if (e.type === 'snapshot') setPrivateSnapshot(e.snapshot);
        else if (e.type === 'audio-blocked') setPrivateAudioBlocked(e.blocked);
        else dropped(e.reason === 'ended' ? 'Комната закрыта' : 'Связь с комнатой потеряна');
      });
      requestPrivateTicket({ tournamentId: room.tournamentId, userId })
        .then((ticket) => privateClient.connect(ticket, micSource.clone(), refMicOn.current))
        .then(() => {
          // вошли в приват — только теперь фокус туда: общие комнаты не слушаем и в них
          // не говорим; не вошли — все как было
          if (refPrivateClient.current !== privateClient) return;
          refFocusPrivate.current = true;
          refClients.current.forEach((c) => {
            c.setAudioOn(false);
            c.setVideoOn(false);
            c.setMicOn(false).catch(FUNC_NOOP);
          });
          refPrivateWith.current = roomKey(room);
          client.setPrivateWith(userId).catch(FUNC_NOOP);
        })
        .catch((e: unknown) => dropped(e instanceof Error ? e.message : 'Не удалось подключиться'));
    },
    [roomOf, log, privateSpeakingProxy, micSource, requestPrivateTicket],
  );

  const closePrivate = useCallback(() => {
    if (refPrivateClient.current) log.event('close_private');
    resetPrivate();
    // фокус обратно в общие комнаты; в приват не входили — трогать нечего
    if (!refFocusPrivate.current) return;
    refFocusPrivate.current = false;
    refClients.current.forEach((c, key) => {
      c.setAudioOn(true);
      c.setVideoOn(true);
      c.setMicOn(refMicOn.current).catch(FUNC_NOOP);
      if (key === refPrivateWith.current) c.setPrivateWith(undefined).catch(FUNC_NOOP);
    });
    refPrivateWith.current = null;
  }, [log, resetPrivate]);

  // ---- Действия судьи

  // Действие судьи: событие в журнал, провал — ошибкой туда же и уведомлением
  const act = useCallback(
    (action: string, promise: Promise<unknown>) => {
      log.event(action);
      promise.catch((e: unknown) => {
        log.error('action_failed', e, { action });
        report(e instanceof Error ? e : new Error(String(e)));
      });
    },
    [log, report],
  );

  // Потолок слоя сетки снимаем только закрепленным
  const setPinned = useCallback((userIds: string[]) => {
    refPinned.current = userIds;
    refClients.current.forEach((c) => c.setPinned(userIds));
  }, []);

  // Видео общих комнат — только игрокам с плитками; приватный клиент показывает всех
  const setShown = useCallback((userIds: string[]) => {
    refShown.current = userIds;
    refClients.current.forEach((c) => c.setShown(userIds));
  }, []);

  // Мьют по устройству: в привате — его клиент, иначе та комната, где устройство
  const mute = useCallback(
    (_userId: string, deviceId: string) => {
      const client =
        refPrivateClient.current ??
        [...refClients.current.values()].find((c) => c.hasParticipant(deviceId));
      if (client) act('mute', client.muteDevice(deviceId));
    },
    [act],
  );

  const kick = useCallback(
    (userId: string, at?: RoomRef | null) => {
      const room = roomOf(userId, at);
      const client = room && refClients.current.get(roomKey(room));
      if (!room || !client) return;
      const key = roomKey(room);
      setBanned((prev) => new Map(prev).set(key, new Set(prev.get(key)).add(userId)));
      // сервер связи сам отключает устройства забаненного
      act('kick', client.ban(userId));
    },
    [act, roomOf],
  );

  const restore = useCallback(
    (userId: string, at?: RoomRef | null) => {
      const room = roomOf(userId, at);
      const client = room && refClients.current.get(roomKey(room));
      if (!room || !client) return;
      const key = roomKey(room);
      setBanned((prev) => {
        const next = new Set(prev.get(key));
        next.delete(userId);
        return new Map(prev).set(key, next);
      });
      act('restore', client.unban(userId));
    },
    [act, roomOf],
  );

  const invitePrivate = useCallback(
    (userId: string, at?: RoomRef | null) => {
      const room = roomOf(userId, at);
      if (!room) return;
      patchMember(room.tournamentId, userId, { inPrivateRoom: true });
      act(
        'invite_private',
        updateAux({ tournamentId: room.tournamentId, userId, aux: { privateVideoRoom: true } }),
      );
      openPrivate(userId, room);
    },
    [roomOf, act, updateAux, openPrivate, patchMember],
  );

  const returnToRoom = useCallback(
    (userId: string, at?: RoomRef | null) => {
      const room = roomOf(userId, at);
      if (!room) return;
      patchMember(room.tournamentId, userId, { inPrivateRoom: false });
      act('return_to_room', updateAux({ tournamentId: room.tournamentId, userId, aux: null }));
    },
    [roomOf, act, updateAux, patchMember],
  );

  // ---- Сборка списков

  const names = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((byUser) => byUser.forEach((m) => map.set(m.userId, m.name)));
    return map;
  }, [members]);

  // Судьи всех комнат без повторов: себя и коллег в нескольких комнатах показываем один раз
  const referees = useMemo(() => {
    const seen = new Set<string>();
    const list: RefereeInfo[] = [];
    Object.values(roomsSnap).forEach((state) => {
      state.snapshot.hosts.forEach((h) => {
        if (seen.has(h.uid)) return;
        seen.add(h.uid);
        list.push(toReferee(h, names));
      });
    });
    return list;
  }, [roomsSnap, names]);

  // В списке все участники комнаты из ее турнира плюс все, кто реально подключен: забаненный
  // в турнире или снятый с него игрок с устройствами в комнате виден, чтобы судья мог
  // удалить его руками (в штатной ситуации его отключит собственный браузер)
  const players = useMemo<RefereePlayer[]>(() => {
    return rooms.flatMap((room) => {
      const key = roomKey(room);
      const snapshot = roomsSnap[key]?.snapshot ?? EMPTY_SNAPSHOT;
      const bannedHere = banned.get(key) ?? new Set<string>();
      const gamesByUser = gamesByTournament.get(room.tournamentId) ?? new Map<string, RoundGame>();
      const devicesByUser = new Map<string, RoomDevice[]>();
      snapshot.devices.forEach((d) => {
        devicesByUser.set(d.uid, [...(devicesByUser.get(d.uid) ?? []), d]);
      });

      const listed = [...(members.get(room.tournamentId)?.values() ?? [])].filter(
        (m) =>
          m.videoRoomNumber === room.roomNumber &&
          (m.status === 'Participating' || devicesByUser.has(m.userId)),
      );
      const known = new Set(listed.map((m) => m.userId));
      const strangers = [...devicesByUser.keys()]
        .filter((uid) => !known.has(uid))
        .map((uid) => ({
          userId: uid,
          name: names.get(uid) ?? devicesByUser.get(uid)![0]!.name ?? 'Без имени',
          inPrivateRoom: false,
          camerasRequired: DEFAULT_CAMERAS,
        }));

      return [...listed, ...strangers].map((p): RefereePlayer => {
        const game = gamesByUser.get(p.userId);
        return {
          key: `${room.tournamentId}:${p.userId}`,
          userId: p.userId,
          name: p.name,
          tournamentId: room.tournamentId,
          roomNumber: room.roomNumber,
          playing: !!game && game.status !== 'Finished',
          gameId: game?.gameId,
          camerasRequired: p.camerasRequired,
          devices: sortDevices(devicesByUser.get(p.userId) ?? []).map((d) => ({
            id: d.identity,
            slot: d.slot,
            attach: d.attach,
            stream: d.stream,
            micOn: d.micOn,
            camOn: d.camOn,
          })),
          removed: bannedHere.has(p.userId),
          inPrivateRoom: p.inPrivateRoom,
          privateReferees: snapshot.hosts
            .filter((h) => h.privateWith === p.userId)
            .map((h) => h.name),
        };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsKey, roomsSnap, banned, gamesByTournament, members, names]);
  refPlayers.current = players;

  const privateRoom = useMemo<PrivateRoom | null>(() => {
    if (!privateUserId) return null;
    const key = privateAt ? `${privateAt.tournamentId}:${privateUserId}` : privateUserId;
    const base =
      players.find((p) => p.key === key) ?? players.find((p) => p.userId === privateUserId);
    const player: RefereePlayer = {
      key,
      userId: privateUserId,
      name: base?.name ?? names.get(privateUserId) ?? '',
      tournamentId: privateAt?.tournamentId ?? base?.tournamentId,
      roomNumber: privateAt?.roomNumber ?? base?.roomNumber,
      playing: base?.playing ?? false,
      gameId: base?.gameId,
      removed: false,
      inPrivateRoom: true,
      // сколько плиток ждать: требование игрока, но не меньше, чем устройств было в общей
      camerasRequired: Math.max(
        base?.camerasRequired ?? DEFAULT_CAMERAS,
        base?.devices.length ?? 0,
      ),
      devices: sortDevices(privateSnapshot.devices.filter((d) => d.uid === privateUserId)).map(
        (d) => ({
          id: d.identity,
          slot: d.slot,
          attach: d.attach,
          stream: d.stream,
          micOn: d.micOn,
          camOn: d.camOn,
        }),
      ),
    };
    return {
      player,
      connection: privateConnection,
      error: privateError,
      referees: privateSnapshot.hosts.map((h) => toReferee(h, names)),
      meterTrack,
      speakingProxy: privateSpeakingProxy,
    };
  }, [
    privateUserId,
    privateAt,
    players,
    names,
    privateSnapshot,
    privateConnection,
    privateError,
    meterTrack,
    privateSpeakingProxy,
  ]);

  // Сводное состояние и комнаты для плитки
  const states = rooms.map((room) => roomsSnap[roomKey(room)]);
  const connection: RefereeConnection = !joined
    ? 'setup'
    : states.some((s) => s?.connection === 'reconnecting')
      ? 'reconnecting'
      : states.some((s) => s?.connection === 'live')
        ? 'live'
        : states.some((s) => !s || s.connection === 'connecting')
          ? 'connecting'
          : 'error';
  const roomsInfo: RefereeRoomInfo[] = rooms.map((room, i) => ({
    ...room,
    title: titles.get(room.tournamentId) ?? '',
    connection: states[i]?.connection ?? 'connecting',
    error: states[i]?.error ?? null,
  }));
  const anyAudioBlocked = states.some((s) => s?.audioBlocked) || privateAudioBlocked;

  return {
    connection,
    error: connection === 'error' ? (states.find((s) => s?.error)?.error ?? null) : null,
    rooms: roomsInfo,
    devices,
    micOn,
    players,
    referees,
    speakingProxy,
    join,
    leave,
    retryRoom,
    toggleMic,
    // плашка связи важнее: пока любая из комнат восстанавливается, беду с микрофоном не показываем
    micIssue:
      connection === 'reconnecting' || privateConnection === 'reconnecting' ? null : micIssue,
    fixMic,
    audioBlocked: anyAudioBlocked,
    meterTrack,
    setPinned,
    setShown,
    mute,
    kick,
    restore,
    invitePrivate,
    returnToRoom,
    privateRoom,
    openPrivate,
    closePrivate,
  };
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

type RoundGame = { gameId: string; status: string; white: { id: string }; black: { id: string } };

// Самая активная партия игрока в туре: в нокауте у пары бывает несколько
const GAME_RANK: Record<string, number> = { InProgress: 3, Paused: 2, NotStarted: 1 };

function indexGames(games?: RoundGame[]): Map<string, RoundGame> {
  const map = new Map<string, RoundGame>();
  games?.forEach((g) => {
    [g.white.id, g.black.id].forEach((userId) => {
      const prev = map.get(userId);
      if (!prev || (GAME_RANK[g.status] ?? 0) > (GAME_RANK[prev.status] ?? 0)) map.set(userId, g);
    });
  });
  return map;
}

function toReferee(h: RoomSnapshot['hosts'][number], names: Map<string, string>): RefereeInfo {
  return {
    id: h.identity,
    name: h.name,
    isMe: h.isMe,
    micOn: h.micOn,
    privateWith: h.privateWith
      ? { userId: h.privateWith, name: names.get(h.privateWith) ?? '' }
      : undefined,
  };
}

// Затравка модели участников из списка с сервера; назначения комнат по событиям — поверх
function seedMembers(
  participants: components['schemas']['ParticipantDto'][],
  assigned: ReadonlyMap<string, number>,
): Map<string, Member> {
  return new Map(
    participants.map((p) => [
      p.userId,
      {
        userId: p.userId,
        name: formatName(p),
        status: p.status,
        videoRoomNumber: assigned.get(p.userId) ?? p.videoRoomNumber,
        inPrivateRoom: parseParticipantAux(p.aux).privateVideoRoom === true,
        camerasRequired: parseMemberAux(p.memberAux).camerasRequired ?? DEFAULT_CAMERAS,
      },
    ]),
  );
}

// Порядок камер игрока: по роли устройства (основная, дополнительная, без роли), при равных —
// по времени входа. Так номер камеры не зависит от порядка переподключений
function sortDevices(devices: readonly RoomDevice[]): RoomDevice[] {
  return [...devices].sort(
    (a, b) =>
      (SLOT_ORDER[a.slot ?? ''] ?? 2) - (SLOT_ORDER[b.slot ?? ''] ?? 2) ||
      a.joinedAt - b.joinedAt ||
      a.identity.localeCompare(b.identity),
  );
}
